import * as THREE from 'three';
import type { Number3, OctreeGroups, PointPickResult, PointsIntersection } from '../types';
import { PointCloudMaterial } from './PointCloudMaterial';
import { IdAllocator } from './IdAllocator';
import { ViewerLayer } from './ViewerLayer';

export const defaultPickCanvasAreaPercent: number = 5;

export const CUBE_TARGET_SIZE = 128;

/**
 * PickHelper implements point picking.
 *
 * Unique 32 bit ids assigned to all PointCloudNode points.
 * To pick a point we render the scene using picking id colors (32 bit, rgba). We render only pixels around mouse pointer (targetSize).
 * Colors from rendered scene converted to 32 bit picking ids and mapped back to particular point (PointCloudNode geometry buffer + index).
 */
export class PickHelper {
  private static readonly minTargetSize = 5;

  private renderTarget: THREE.WebGLRenderTarget | null = null;

  private pixelBuffer: Uint8Array | null = null;

  private pickingScene = new THREE.Scene();

  private pointCloudNodes = new Map<
    string,
    { sceneOctreeGroup: THREE.Group; points: THREE.Points; allocatedIdRanges: number[] }
  >();

  private idRanges = new Map<
    number,
    {
      octreeId: string;
      key: string;
      pointsObject: THREE.Points;
      geometry: THREE.BufferGeometry;
      offset: number;
      getPointAttribute: (pointIndex: number, attributeName: string) => number | Number3;
      getPointAttributes: (
        pointIndex: number,
        attributeNames: string[]
      ) => Map<string, number | number[]>;
    }
  >();

  // Assigning unique picking ids for all PointCloudNode points (from 32 bit global range).
  // Global range splited in 64K chunks (ranges).
  // Unique picking id (32 bit) consists of two parts: [rangeId, rangePointIndex].
  // One or more ranges get assigned to PointCloudNode points.
  // idRanges contains mapping if rangeId to geometry buffer and offset.
  // Point index in geometry buffer is idRanges.offset + rangePointIndex.
  private static readonly rangeBits = 16;

  private static readonly rangeSize = 1 << PickHelper.rangeBits;

  // excluding 0, to ensure all ids are non-zero, zero used as clear color
  private static readonly minRangeId = 1;

  private static readonly maxRangeId = (1 << (32 - PickHelper.rangeBits)) - 1;

  private rangeIdAllocator = new IdAllocator(PickHelper.minRangeId, PickHelper.maxRangeId);

  private cubeTarget = new THREE.WebGLCubeRenderTarget(CUBE_TARGET_SIZE);

  private cubeCamera = new THREE.CubeCamera(0.1, 1000, this.cubeTarget);

  private cubePixelBuffer = new Uint8Array(4 * CUBE_TARGET_SIZE * CUBE_TARGET_SIZE);

  private octreeGroups: OctreeGroups | undefined = undefined;

  private sceneOctreeGroups: Map<string, THREE.Group> = new Map<string, THREE.Group>();

  // private viewerSphere: THREE.Mesh;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  constructor(_viewerSphereGeometry: THREE.SphereGeometry) {
    // clipping sphere is causing picking issues in Fabric with sparse point clouds
    // this.viewerSphere = new THREE.Mesh(
    //   viewerSphereGeometry,
    //   new THREE.MeshBasicMaterial({
    //     color: new THREE.Color(0),
    //     side: THREE.BackSide,
    //     blending: THREE.NoBlending,
    //   })
    // );
    // this.viewerSphere.layers.disableAll();
    // this.viewerSphere.layers.enable(ViewerLayer.SphericalViewerLayer);
    // this.pickingScene.add(this.viewerSphere);
    this.pickingScene.add(this.cubeCamera);
  }

  setOctreeGroups(octreeGroups: OctreeGroups) {
    this.octreeGroups = octreeGroups;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  enableViewerSphere(_position: Number3, _sphereRadius: number) {
    // viewer sphere hides points outside the sphere in spherical viewer mode
    // this.viewerSphere.layers.enable(ViewerLayer.SphericalViewerLayer);
    // this.viewerSphere.position.set(...position);
    // this.viewerSphere.scale.set(sphereRadius, sphereRadius, sphereRadius);
  }

  disableViewerSphere() {
    // this.viewerSphere.layers.disableAll();
  }

  addPointCloudNode(
    octreeId: string,
    key: string,
    pointsObject: THREE.Points,
    geometry: THREE.BufferGeometry,
    pointCloudMaterial: PointCloudMaterial,
    getPointAttribute: (pointIndex: number, attributeName: string) => number | Number3,
    getPointAttributes: (
      pointIndex: number,
      attributeNames: string[]
    ) => Map<string, number | number[]>
  ) {
    const { pointCloudNodes, pickingScene, rangeIdAllocator, idRanges } = this;

    if (pointCloudNodes.get(key)) {
      throw new Error('PickHelper: key already exists');
    }

    // adding picking ids
    const allocatedIdRanges = [];
    const positions = geometry.getAttribute('position');
    const numberPoints = positions.array.length;
    const cardinality = 4;
    const pickingIds = new Float32Array(numberPoints * cardinality);

    for (let offset = 0; offset < numberPoints; offset += PickHelper.rangeSize) {
      const idRange = rangeIdAllocator.allocate();
      if (idRange === undefined) {
        throw new Error('rangeIdAllocator failed');
      }
      allocatedIdRanges.push(idRange);
      idRanges.set(idRange, {
        octreeId,
        key,
        pointsObject,
        geometry,
        offset,
        getPointAttribute,
        getPointAttributes,
      });
      const rangeEnd = Math.min(offset + PickHelper.rangeSize, numberPoints);
      for (let index = offset; index < rangeEnd; index += 1) {
        const rangePointIndex = index - offset;
        const pickId = idRange * PickHelper.rangeSize + rangePointIndex;
        const bufferOffset = index * cardinality;
        pickingIds[bufferOffset + 0] = (pickId & 0xff) / 255;
        pickingIds[bufferOffset + 1] = ((pickId >> 8) & 0xff) / 255;
        pickingIds[bufferOffset + 2] = ((pickId >> 16) & 0xff) / 255;
        pickingIds[bufferOffset + 3] = ((pickId >> 24) & 0xff) / 255;
      }
    }
    const pickingAttribute = new THREE.Float32BufferAttribute(pickingIds, cardinality);
    geometry.setAttribute('pickingId', pickingAttribute);

    let sceneOctreeGroup = this.sceneOctreeGroups.get(octreeId);
    if (!sceneOctreeGroup) {
      sceneOctreeGroup = new THREE.Group();
      sceneOctreeGroup.matrixAutoUpdate = false;
      sceneOctreeGroup.matrixWorldAutoUpdate = true;
      sceneOctreeGroup.layers.enable(ViewerLayer.SphericalViewerLayer);
      pickingScene.add(sceneOctreeGroup);
      this.sceneOctreeGroups.set(octreeId, sceneOctreeGroup);
    }

    const points = new THREE.Points(geometry, pointCloudMaterial.pickingShaderMaterial);
    points.layers.enable(ViewerLayer.SphericalViewerLayer);
    sceneOctreeGroup.add(points);
    pointCloudNodes.set(key, { sceneOctreeGroup, points, allocatedIdRanges });

    // console.log(`PickHelper.addPointCloudNode ${key}   ${pointCloudNodes.size}`);
  }

  removePointCloudNode(key: string) {
    const { pointCloudNodes, idRanges, rangeIdAllocator } = this;
    const pointCloud = pointCloudNodes.get(key);
    if (pointCloud !== undefined) {
      const { sceneOctreeGroup, points, allocatedIdRanges } = pointCloud;
      allocatedIdRanges.forEach((idRange) => {
        idRanges.delete(idRange);
        rangeIdAllocator.release(idRange);
      });

      sceneOctreeGroup.remove(points);
      points.geometry.deleteAttribute('pickingId');
      pointCloudNodes.delete(key);

      // console.log(`PickHelper.removePointCloudNode ${key}  ${pointCloudNodes.size}`);
    }
  }

  setTargetSize(targetSize: number): void {
    if (this.renderTarget === null || this.renderTarget.height !== targetSize) {
      this.renderTarget = new THREE.WebGLRenderTarget(targetSize, targetSize);
      this.pixelBuffer = new Uint8Array(4 * targetSize * targetSize);
    }
  }

  scanPixelBufferFromCenter(halfTargetSize: number, targetSize: number): number {
    const middle = halfTargetSize;
    for (let boxHalfSize = 0; boxHalfSize <= halfTargetSize; boxHalfSize += 1) {
      // scanning perimeter of the box (of 2 * boxHalfSize + 1 size)
      for (const coord0 of [middle - boxHalfSize, middle + boxHalfSize]) {
        for (let coord1 = middle - boxHalfSize; coord1 <= middle + boxHalfSize; coord1 += 1) {
          for (const pixelIndex of [coord0 * targetSize + coord1, coord1 * targetSize + coord0]) {
            const id = this.getPixelAsId(this.pixelBuffer!, pixelIndex);
            if (id !== 0) {
              return id;
            }
          }
        }
      }
    }
    return 0;
  }

  updateWorldMatrices() {
    this.octreeGroups?.forEach(({ group }, octreeId) => {
      const sceneGroup = this.sceneOctreeGroups.get(octreeId);
      if (sceneGroup && group) {
        sceneGroup.matrix.copy(group.matrixWorld);
        sceneGroup.matrixWorldNeedsUpdate = true;
      }
    });
  }

  pickObject(
    centerX: number,
    centerY: number,
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    canvasAreaPercent: number
  ): PointsIntersection | undefined {
    const { pickingScene, idRanges, renderPickingArea } = this;

    const canvas = renderer.domElement;
    const halfTargetSize = Math.max(
      Math.trunc(PickHelper.minTargetSize / 2),
      Math.round((canvas.height * canvasAreaPercent) / 200)
    );
    // assuming odd targetSize in scanPixelBufferFromCenter
    const targetSize = 2 * halfTargetSize + 1;
    this.setTargetSize(targetSize);
    const { renderTarget, pixelBuffer } = this;

    const pixelRatio = renderer.getPixelRatio();
    const x = centerX / pixelRatio;
    const y = centerY / pixelRatio;

    const { drawingBufferWidth, drawingBufferHeight } = renderer.getContext();

    renderPickingArea(
      camera,
      renderer,
      renderTarget!,
      pixelBuffer!,
      pickingScene,
      drawingBufferHeight,
      drawingBufferWidth,
      x,
      y,
      pixelRatio
    );

    const id = this.scanPixelBufferFromCenter(halfTargetSize, targetSize);

    if (id === 0) {
      return undefined;
    }

    const rangeId = id >> PickHelper.rangeBits;

    const rangeData = idRanges.get(rangeId);
    if (rangeData !== undefined) {
      const { pointsObject, offset, getPointAttributes, getPointAttribute } = rangeData;
      const rangePointIndex = id - rangeId * PickHelper.rangeSize;
      const pointIndex = offset + rangePointIndex;

      const position: Number3 = getPointAttribute(pointIndex, 'position') as Number3;
      const point = new THREE.Vector3(...position);

      const intersection: PointsIntersection = {
        distance: camera.position.distanceTo(point),
        point,
        object: pointsObject,
        index: pointIndex,
        pointPickInfo: { pointIndex, getPointAttribute, getPointAttributes },
      };

      return intersection;
    }

    return undefined;
  }

  pick(
    mouseEvent: PointerEvent | MouseEvent,
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    renderer: THREE.WebGLRenderer,
    canvasAreaPercent: number
  ): PointPickResult | undefined {
    const canvas = renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    const { clientX, clientY } = mouseEvent;
    const centerX = ((clientX - rect.left) * canvas.width) / rect.width;
    const centerY = ((clientY - rect.top) * canvas.height) / rect.height;

    const intersection = this.pickObject(centerX, centerY, camera, renderer, canvasAreaPercent);

    if (intersection) {
      const { pointPickInfo } = intersection;
      return { ...pointPickInfo, mouseEvent };
    }

    return undefined;
  }

  pickPoints(
    mouseEvent: PointerEvent | MouseEvent,
    selectBox: { x: number; y: number; width: number; height: number },
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    renderer: THREE.WebGLRenderer
  ) {
    const width = Math.trunc(selectBox.width);
    const height = Math.trunc(selectBox.height);
    const { pickingScene, idRanges, renderPickingArea, getPixelAsId } = this;
    const boxRenderTarget = new THREE.WebGLRenderTarget(width, height);
    const boxPixelBuffer = new Uint8Array(4 * width * height);

    const canvas = renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    const pixelRatio = renderer.getPixelRatio();
    const clientX = Math.trunc(width / 2 + selectBox.x);
    const clientY = Math.trunc(height / 2 + selectBox.y);
    const x = ((clientX - rect.left) * canvas.width) / (rect.width * pixelRatio);
    const y = ((clientY - rect.top) * canvas.height) / (rect.height * pixelRatio);

    const { drawingBufferWidth, drawingBufferHeight } = renderer.getContext();

    renderPickingArea(
      camera,
      renderer,
      boxRenderTarget,
      boxPixelBuffer,
      pickingScene,
      drawingBufferHeight,
      drawingBufferWidth,
      x,
      y,
      pixelRatio
    );

    const results: PointPickResult[] = [];
    for (let index = 0; 4 * index < boxPixelBuffer.length; index += 1) {
      const pixelId = getPixelAsId(boxPixelBuffer, index);
      const rangeId = Math.trunc(pixelId / PickHelper.rangeSize);
      const rangeData = idRanges.get(rangeId);
      if (rangeData !== undefined) {
        const { offset, getPointAttribute, getPointAttributes } = rangeData;
        const rangePointIndex = pixelId - rangeId * PickHelper.rangeSize;
        const pointIndex = offset + rangePointIndex;
        results.push({ pointIndex, getPointAttribute, getPointAttributes, mouseEvent });
      }
    }

    return results;
  }

  public getPointAttributeGetter(id: number) {
    const rangeId = id >> PickHelper.rangeBits;
    const rangeData = this.idRanges.get(rangeId);
    if (rangeData === undefined) {
      return undefined;
    }
    const { offset, getPointAttribute } = rangeData;
    const rangePointIndex = id - rangeId * PickHelper.rangeSize;
    const pointIndex = offset + rangePointIndex;

    return (attributeName: string) => getPointAttribute(pointIndex, attributeName);
  }

  public findFocusPoint(
    cameraPosition: Number3,
    attributeName: string,
    attributeValues: number[],
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    renderer: THREE.WebGLRenderer
  ): Number3 | undefined {
    const { cubeCamera, pickingScene, cubeTarget, cubePixelBuffer, getPixelAsId } = this;
    cubeCamera.position.set(...cameraPosition);
    cubeCamera.layers.mask = camera.layers.mask;
    // renders 6 cube faces into this.cubeTarget
    cubeCamera.update(renderer, pickingScene);

    const valueLookup = new Set(attributeValues);

    let [cx, cy, cz] = [0, 0, 0];
    let count = 0;

    const pointPositions: Number3[] = [];

    for (let faceIndex = 0; faceIndex < 6; faceIndex += 1) {
      renderer.readRenderTargetPixels(
        cubeTarget,
        0, // x
        0, // y
        CUBE_TARGET_SIZE, // width
        CUBE_TARGET_SIZE, // height
        cubePixelBuffer,
        faceIndex
      );
      for (let x = 0; x <= CUBE_TARGET_SIZE; x += 1) {
        for (let y = 0; y <= CUBE_TARGET_SIZE; y += 1) {
          const pixelIndex = y * CUBE_TARGET_SIZE + x;
          const id = getPixelAsId(cubePixelBuffer, pixelIndex);
          if (id !== 0) {
            const getAttribute = this.getPointAttributeGetter(id);
            if (getAttribute) {
              const pointValue = getAttribute(attributeName);
              if (!Array.isArray(pointValue) && valueLookup.has(pointValue)) {
                const position = getAttribute('position');
                if (Array.isArray(position) && position.length === 3) {
                  const [px, py, pz] = position;
                  cx += px;
                  cy += py;
                  cz += pz;
                  count += 1;
                  pointPositions.push(position);
                }
              }
            }
          }
        }
      }
    }

    if (count === 0) {
      return undefined;
    }
    const invCount = 1 / count;
    cx *= invCount;
    cy *= invCount;
    cz *= invCount;

    const calcDistanceFromCenter = (position: Number3) => {
      const [x, y, z] = position;
      const dx = x - cx;
      const dy = y - cy;
      const dz = z - cz;
      return dx * dx + dy * dy + dz * dz;
    };

    const pointClosestToCenter = pointPositions.reduce((previous, current) => {
      return calcDistanceFromCenter(previous) <= calcDistanceFromCenter(current)
        ? previous
        : current;
    }, pointPositions[0]);

    return pointClosestToCenter;
  }

  public dispose() {
    this.pointCloudNodes.forEach((_node, key) => {
      this.removePointCloudNode(key);
    });
    this.renderTarget?.dispose();
    this.cubeTarget.dispose();
    // this.viewerSphere.material.dispose();
  }

  public getPixelAsId = (pixelBuffer: Uint8Array, index: number) => {
    return (
      (pixelBuffer[4 * index + 3] << 24) |
      (pixelBuffer[4 * index + 2] << 16) |
      (pixelBuffer[4 * index + 1] << 8) |
      pixelBuffer[4 * index + 0]
    );
  };

  // Renders a subsection of the camera with the specified center, width, height (picking area).
  // The pixels of picking area are decoded to get point data for any point within the picking area.
  private renderPickingArea = (
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    renderTarget: THREE.WebGLRenderTarget,
    pixelBuffer: Uint8Array,
    pickingScene: THREE.Scene,
    drawingBufferHeight: number,
    drawingBufferWidth: number,
    x: number,
    y: number,
    pixelRatio: number
  ) => {
    if (
      !(camera instanceof THREE.PerspectiveCamera || camera instanceof THREE.OrthographicCamera)
    ) {
      return;
    }
    this.updateWorldMatrices();

    const areaWidth = renderTarget.width;
    const areaHeight = renderTarget.height;
    camera.setViewOffset(
      drawingBufferWidth, // full width
      drawingBufferHeight, // full top
      x * pixelRatio - areaWidth / 2, // center x
      y * pixelRatio - areaHeight / 2, // center y
      areaWidth, // rect width
      areaHeight // rect height
    );

    // render the scene
    renderer.autoClear = true;
    renderer.setRenderTarget(renderTarget);
    renderer.render(pickingScene, camera);
    renderer.setRenderTarget(null);

    // clear the view offset so rendering returns to normal
    camera.clearViewOffset();

    renderer.readRenderTargetPixels(
      renderTarget,
      0, // x
      0, // y
      areaWidth, // width
      areaHeight, // height
      pixelBuffer
    );
  };

  public renderFrame = (
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    renderTarget: THREE.WebGLRenderTarget,
    pixelBuffer: Uint8Array
  ) => {
    if (
      !(camera instanceof THREE.PerspectiveCamera || camera instanceof THREE.OrthographicCamera)
    ) {
      return;
    }
    this.updateWorldMatrices();

    const { pickingScene } = this;
    const areaWidth = renderTarget.width;
    const areaHeight = renderTarget.height;

    // render the scene
    renderer.autoClear = true;
    renderer.setRenderTarget(renderTarget);
    renderer.render(pickingScene, camera);
    renderer.setRenderTarget(null);

    renderer.readRenderTargetPixels(
      renderTarget,
      0, // x
      0, // y
      areaWidth, // width
      areaHeight, // height
      pixelBuffer
    );
  };
}

export default PickHelper;
